import { useState, useEffect } from "react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentDuplicateIcon,
  ClockIcon,
  FolderOpenIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";

import { legislationsAPI } from "../../../services/api";

function LegislationOverview() {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await legislationsAPI.getDashboard();
        const d = res.data.data.legislation_overview;

        // ----- Ordinance Status -----
        const ordinanceStatus = Object.entries(d.ordinance_status).map(
          ([status, value]) => {
            const numberValue = Number(value.split(" ")[0]); // extract number
            return {
              status,
              value: numberValue,
              percentage: 0, // computed later
              color:
                status.toLowerCase() === "draft"
                  ? "bg-gray-400"
                  : status.toLowerCase() === "pending"
                  ? "bg-yellow-500"
                  : status.toLowerCase() === "approved"
                  ? "bg-green-600"
                  : status.toLowerCase() === "implemented"
                  ? "bg-blue-600"
                  : "bg-red-600",
            };
          }
        );

        const ordinanceTotal = ordinanceStatus.reduce((a, b) => a + b.value, 0);
        ordinanceStatus.forEach((item) => {
          item.percentage = ordinanceTotal
            ? ((item.value / ordinanceTotal) * 100).toFixed(1)
            : 0;
        });

        // ----- Participation Demographics -----
        const participationDemographics = Object.entries(
          d.participation_demographics
        ).map(([name, value]) => {
          const numberValue = Number(value.split(" ")[0]);
          return {
            name: name.replace(/_/g, " "),
            value: numberValue,
            percentage: 0, // compute later
            color: "text-red-600",
          };
        });

        const participationTotal = participationDemographics.reduce(
          (a, b) => a + b.value,
          0
        );
        participationDemographics.forEach((item) => {
          item.percentage = participationTotal
            ? ((item.value / participationTotal) * 100).toFixed(1)
            : 0;
        });

        // ----- Categories -----
        const categories = Object.entries(d.categories).map(([name, value]) => ({
          name,
          value,
        }));

        // ----- Consultation Activity -----
        const consultationActivity = Object.entries(d.consultancy_activity).map(
          ([name, value]) => ({
            name: name.replace(/_/g, " "),
            value,
          })
        );

        // ----- Legislation Timeline -----
        const legislationTimeline = d.legislation_timeline.map((t) => ({
          year: t.year,
          resolutions: Number(t.resolutions),
          ordinances: Number(t.ordinances),
        }));

        setData({
          ordinanceStatus,
          categories,
          participationDemographics,
          consultationActivity,
          legislationTimeline,
        });
      } catch (err) {
        console.error("Dashboard load error: ", err);
      }
    };

    loadData();
  }, []);

  if (!data)
    return (
      <div className="bg-white shadow-lg rounded-lg mt-8 mb-6 p-6 border border-gray-200">
        Loading Overview…
      </div>
    );

  return (
    <div className="bg-white shadow-lg rounded-lg mt-8 mb-6 overflow-hidden border border-gray-200">
      <div
        className="px-6 py-4 flex justify-between items-center cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center space-x-4">
          <DocumentDuplicateIcon className="w-6 h-6 text-blue-600" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Legislation Overview
            </h2>
            <p className="text-sm text-gray-600">
              Breakdown of resolutions, ordinances, and consultations by type,
              status, and participation.
            </p>
          </div>
        </div>
        {isOpen ? (
          <ChevronUpIcon className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
        )}
      </div>

      {isOpen && (
        <div className="p-6 pt-4 border-t border-gray-100 animate-fadeIn">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Ordinance Status */}
            <div className="p-4 bg-blue-100 shadow-sm rounded-lg border border-blue-200">
              <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                <ClockIcon className="w-7 h-7 mr-2 text-blue-700" />
                Ordinance Status
              </h3>
              {data.ordinanceStatus.map((item) => (
                <div key={item.status} className="mb-3">
                  <div className="flex justify-between text-xs text-gray-700 mb-1">
                    <span>{item.status}</span>
                    <span className="font-medium">
                      {item.value} ({item.percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2.5">
                    <div
                      className={`h-2.5 rounded-full ${item.color}`}
                      style={{ width: `${item.percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Categories */}
            <div className="p-4 bg-green-50 shadow-sm rounded-lg border border-green-200">
              <div className="flex items-center text-green-700 mb-4">
                <FolderOpenIcon className="w-7 h-7 mr-2" />
                <h3 className="text-base font-semibold text-gray-900">
                  Categories
                </h3>
              </div>
              <ul className="space-y-2">
                {data.categories.map((item) => (
                  <li
                    key={item.name}
                    className="flex justify-between items-center text-sm text-gray-900"
                  >
                    <span>{item.name}</span>
                    <span className="font-medium text-green-900">{item.value}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Participation Demographics */}
            <div className="p-4 bg-red-50 shadow-sm rounded-lg border border-red-200">
              <div className="flex items-center text-red-700 mb-4">
                <UserGroupIcon className="w-7 h-7 mr-2" />
                <h3 className="text-base font-semibold text-red-800">
                  Participation Demographics
                </h3>
              </div>
              {data.participationDemographics.map((item) => (
                <div
                  key={item.name}
                  className="flex justify-between items-center text-sm text-gray-700 mb-2"
                >
                  <span>{item.name}</span>
                  <span className={`font-medium ${item.color}`}>
                    {item.value} ({item.percentage}%)
                  </span>
                </div>
              ))}
            </div>

            {/* Consultation Activity */}
            <div className="p-4 bg-teal-50 shadow-sm rounded-lg border border-teal-200">
              <div className="flex items-center text-teal-700 mb-4">
                <ChatBubbleLeftRightIcon className="w-7 h-7 mr-2" />
                <h3 className="text-base font-semibold text-teal-800">
                  Consultation Activity
                </h3>
              </div>
              <ul className="space-y-2">
                {data.consultationActivity.map((item) => (
                  <li
                    key={item.name}
                    className="flex justify-between items-center text-sm text-gray-700"
                  >
                    <span>{item.name}</span>
                    <span className="font-medium text-blue-600">{item.value}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legislation Timeline */}
            <div className="lg:col-span-2 p-4 bg-orange-50 shadow-sm rounded-lg border border-orange-200">
              <div className="flex items-center text-orange-700 mb-4">
                <CalendarIcon className="w-7 h-7 mr-2" />
                <h3 className="text-base font-semibold text-orange-800">
                  Legislation Timeline
                </h3>
              </div>
              <table className="min-w-full divide-y divide-orange-300">
                <thead>
                  <tr className="text-sm font-semibold text-orange-900">
                    <th className="py-2 text-left px-3">Year</th>
                    <th className="py-2 text-right">Resolutions</th>
                    <th className="py-2 text-right pr-3">Ordinances</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-orange-200">
                  {data.legislationTimeline.map((item) => (
                    <tr
                      key={item.year}
                      className="text-sm text-gray-700 hover:bg-orange-100 transition-colors"
                    >
                      <td className="py-2 font-medium px-3">{item.year}</td>
                      <td className="py-2 text-right text-orange-600">
                        {item.resolutions}
                      </td>
                      <td className="py-2 text-right pr-3 text-orange-600">
                        {item.ordinances}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LegislationOverview;
